import { useState } from "react";
import { Link } from "wouter";
import { format } from "date-fns";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  Wallet,
  Users,
  DollarSign,
  Building2,
  Target,
  Gift,
  Heart,
  AlertTriangle,
  Clock,
  Trash2,
  ShieldAlert,
  CheckCircle,
  CalendarClock,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { StatCard, formatCents } from "./shared";

function TestAccountsCard() {
  const [dryRunResult, setDryRunResult] = useState<{ deleted: number; accounts?: any[] } | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const { data: stats, isLoading } = useQuery<{
    count: number;
    lastCleanupRun: string | null;
    lastCleanupCount: number;
    nextScheduledRun: string;
  }>({
    queryKey: ["/api/admin/test-accounts/stats"],
    refetchInterval: 60_000,
  });

  const dryRunMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/test-accounts/cleanup", { dryRun: true }),
    onSuccess: (data: any) => {
      setDryRunResult(data);
      setShowConfirm(true);
    },
  });

  const cleanupMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/test-accounts/cleanup", { dryRun: false }),
    onSuccess: () => {
      setDryRunResult(null);
      setShowConfirm(false);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/test-accounts/stats"] });
    },
  });

  const nextRun = stats?.nextScheduledRun ? new Date(stats.nextScheduledRun) : null;

  return (
    <Card className="col-span-1">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-yellow-600" />
          Test Accounts
        </CardTitle>
        <CardDescription>Accounts matching automated test patterns</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3">
          <div
            className="text-3xl font-bold"
            data-testid="text-test-account-count"
          >
            {isLoading ? "—" : (stats?.count ?? 0)}
          </div>
          <span className="text-sm text-muted-foreground mb-1">matching accounts</span>
        </div>

        <div className="space-y-1.5 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 shrink-0" />
            <span>
              Last cleanup:{" "}
              {stats?.lastCleanupRun
                ? `${format(new Date(stats.lastCleanupRun), "MMM d, h:mm a")} — removed ${stats.lastCleanupCount}`
                : "never run"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CalendarClock className="w-3.5 h-3.5 shrink-0" />
            <span>
              Next auto-run:{" "}
              {nextRun ? format(nextRun, "EEE MMM d 'at' 2:00 a") : "—"}
            </span>
          </div>
        </div>

        <Separator />

        {!showConfirm ? (
          <Button
            variant="outline"
            size="default"
            className="w-full min-h-[44px]"
            onClick={() => dryRunMutation.mutate()}
            disabled={dryRunMutation.isPending}
            data-testid="button-dry-run-cleanup"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {dryRunMutation.isPending ? "Scanning…" : "Preview Cleanup (Dry Run)"}
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 p-3 space-y-1.5">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                Dry run complete — {dryRunResult?.deleted ?? 0} accounts would be removed
              </p>
              {dryRunResult?.accounts && dryRunResult.accounts.length > 0 && (
                <ul className="text-xs text-yellow-700 dark:text-yellow-400 space-y-0.5 max-h-28 overflow-y-auto">
                  {dryRunResult.accounts.slice(0, 10).map((a: any) => (
                    <li key={a.id} className="truncate">
                      {a.email} ({a.role})
                    </li>
                  ))}
                  {dryRunResult.accounts.length > 10 && (
                    <li className="italic">…and {dryRunResult.accounts.length - 10} more</li>
                  )}
                </ul>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                size="sm"
                className="flex-1"
                onClick={() => cleanupMutation.mutate()}
                disabled={cleanupMutation.isPending}
                data-testid="button-confirm-cleanup"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {cleanupMutation.isPending ? "Removing…" : "Confirm & Remove"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowConfirm(false); setDryRunResult(null); }}
                data-testid="button-cancel-cleanup"
              >
                Cancel
              </Button>
            </div>
            {cleanupMutation.isSuccess && (
              <p className="text-xs text-green-700 dark:text-green-400 flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5" />
                Cleanup complete
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function OverviewDashboard({ users, wallets, challenges, referralCodes, auditLogs, hospitalPartners, conditionWalletsDashboard, platformStats }: any) {
  const activeWallets = wallets.filter((w: any) => (w.balance || 0) > 0).length;
  const activeChallenges = challenges.filter((c: any) => c.isActive).length;
  const totalReferrals = referralCodes.reduce((sum: number, r: any) => sum + (r.timesUsed || 0), 0);
  const recentAlerts = auditLogs.filter((l: any) => l.severity === "warning" || l.severity === "critical").slice(0, 5);
  const hospitalPartnerCount = hospitalPartners?.length || 0;

  // Use server-side platform stats (authoritative DB aggregates) when available,
  // falling back to client-side sums if the endpoint hasn't loaded yet.
  const serverWalletBalance = platformStats?.wallets?.totalBalance;
  const serverWalletContributions = platformStats?.wallets?.totalContributions;
  const serverActiveUsers = platformStats?.users?.active;
  const serverTotalUsers = platformStats?.users?.total;
  const serverConditionRaised = platformStats?.conditionFunds?.totalRaised;

  // Fallback to client-side for loading state
  const totalWalletBalance = serverWalletBalance ?? wallets.reduce((sum: number, w: any) => sum + (w.balance || 0), 0);
  const totalActiveUsers = serverActiveUsers ?? users.length;

  // conditionStats from the dedicated dashboard endpoint (kept for member/pending counts)
  const conditionStats = conditionWalletsDashboard?.kpis || {};
  // Prefer server platform-stats for the raised total (avoids API failure showing $0)
  const conditionTotalRaised = serverConditionRaised ?? conditionStats.totalRaised ?? 0;

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active Wallets"
          value={activeWallets}
          description="Wallets with spendable balance"
          icon={Wallet}
          trend="+8%"
          trendDirection="up"
          color="blue"
        />
        <StatCard
          title="Hospital Partners"
          value={hospitalPartnerCount}
          description="Registered hospital partners"
          icon={Building2}
          color="green"
        />
        <StatCard
          title="Wallet Spendable Balance"
          value={formatCents(totalWalletBalance)}
          description={serverWalletContributions != null ? `${formatCents(serverWalletContributions)} lifetime raised` : "Current unspent funds"}
          icon={DollarSign}
          trend="+8%"
          trendDirection="up"
          color="purple"
        />
        <StatCard
          title="Active Users"
          value={totalActiveUsers}
          description={serverTotalUsers != null ? `${serverTotalUsers} registered (incl. soft-deleted)` : "Non-deleted accounts"}
          icon={Users}
          color="primary"
        />
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Active Challenges
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold mb-2">{activeChallenges}</div>
            <p className="text-sm text-muted-foreground">
              {challenges.length} total challenges configured
            </p>
            <Progress value={(activeChallenges / Math.max(challenges.length, 1)) * 100} className="mt-4" />
          </CardContent>
        </Card>

        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gift className="w-5 h-5 text-green-600" />
              Referral Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold mb-2">{totalReferrals}</div>
            <p className="text-sm text-muted-foreground">
              Total referrals used from {referralCodes.length} codes
            </p>
            <div className="flex items-center gap-2 mt-4">
              <Badge variant="secondary">{referralCodes.filter((r: any) => r.isActive).length} active codes</Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary" />
              Condition Funds
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold mb-2" data-testid="text-condition-fund-count">
              {conditionStats.totalConditions || 0}
            </div>
            <p className="text-sm text-muted-foreground" data-testid="text-condition-funds-raised">
              {formatCents(conditionTotalRaised)} total raised
            </p>
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <Badge variant="outline" className="gap-1">
                <Clock className="w-3 h-3" />
                {conditionStats.pendingMembers || 0} pending memberships
              </Badge>
            </div>
            <Button asChild variant="outline" size="default" className="mt-3 w-full min-h-[44px]" data-testid="link-condition-wallets">
              <Link href="/admin-condition-wallets">
                View Condition Wallet Dashboard
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <TestAccountsCard />

        <Card className="col-span-1 md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              Recent Alerts
            </CardTitle>
            <CardDescription>System warnings and critical events</CardDescription>
          </CardHeader>
          <CardContent>
            {recentAlerts.length > 0 ? (
              <div className="space-y-3">
                {recentAlerts.map((alert: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <Badge variant={alert.severity === "critical" ? "destructive" : "secondary"}>
                      {alert.severity}
                    </Badge>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{alert.action}</p>
                      <p className="text-xs text-muted-foreground">{alert.details}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {alert.createdAt && format(new Date(alert.createdAt), "MMM d, HH:mm")}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No recent alerts</p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
